import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';
import Logo from '../assets/images/arka-saydam-logo.png';

const PTInvite = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [inviteData, setInviteData] = useState(null);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form state (PT focus: Macros and Performance)
    const [formData, setFormData] = useState({
        dailyCalories: '',
        proteinMin: '',
        carbsMin: '',
        carbsMax: '',
        fatMax: '',
        portionMultiplier: '1.0',
        notes: ''
    });

    useEffect(() => {
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/diet/invite/${token}`);
            if (response.data.ok) {
                setInviteData(response.data.invite);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Davet linki geçersiz veya süresi dolmuş.';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handlePostpone = async () => {
        // Just update lastViewedAt, don't invalidate token
        try {
            await axios.post(`${API_BASE_URL}/api/diet/invite/${token}/postpone`);
            Swal.fire({
                icon: 'info',
                title: 'Anlaşıldı',
                text: 'Sporcunun bilgileri hazır olduğunda bu linke tekrar dönebilirsiniz.',
                confirmButtonText: 'Tamam'
            }).then(() => {
                window.close(); // Try to close tab
            });
        } catch (err) {
            console.error('Postpone error:', err);
            window.close();
        }
    };

    const handleAccept = () => {
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.dailyCalories || !formData.proteinMin || !formData.carbsMin || !formData.carbsMax || !formData.fatMax) {
            Swal.fire({
                icon: 'warning',
                title: 'Eksik Bilgi',
                text: 'Lütfen tüm hedefleri (Kalori, Protein, Yağ ve Karbonhidrat aralığı) doldurunuz.'
            });
            return;
        }

        setSubmitting(true);
        try {
            const targets = {
                dailyCalories: parseInt(formData.dailyCalories),
                protein_min: formData.proteinMin ? parseInt(formData.proteinMin) : null,
                carbs_range: formData.carbsMin && formData.carbsMax
                    ? [parseInt(formData.carbsMin), parseInt(formData.carbsMax)]
                    : null,
                fat_max: formData.fatMax ? parseInt(formData.fatMax) : null,
                portionMultiplier: parseFloat(formData.portionMultiplier) || 1.0,
                authority: 'performance'
            };

            const response = await axios.post(
                `${API_BASE_URL}/api/diet/update-by-professional/${token}`,
                {
                    targets,
                    notes: formData.notes
                }
            );

            if (response.data.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Başarılı!',
                    text: 'Performans hedefleri başarıyla belirlendi. Sporcunuz artık bu değerlere göre mutfağını yönetebilecek.',
                    confirmButtonText: 'Tamam'
                }).then(() => {
                    window.close();
                });
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Bir hata oluştu.';
            Swal.fire({
                icon: 'error',
                title: 'Hata',
                text: errorMsg
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center animate-in fade-in duration-700">
                    <div className="h-12 w-12 border-[3px] border-blue-100 dark:border-blue-900 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
                <div className="max-w-sm w-full text-center space-y-6">
                    <div className="h-16 w-16 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto">
                        <span className="material-symbols-outlined text-red-500 text-3xl">error_outline</span>
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-xl font-black text-slate-900 dark:text-white">Bağlantı Geçersiz</h2>
                        <p className="text-slate-500 text-sm leading-relaxed">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const Branding = () => (
        <div className="flex flex-col items-center gap-4 py-8 opacity-90 transition-opacity">
            <a href="/" className="flex flex-col items-center gap-2 group">
                <img src={Logo} alt="BBTV" className="h-10 w-auto transition-all duration-500" />
                <p className="text-[10px] font-black text-slate-500 tracking-[0.3em] uppercase">Bana Bir Tarif Ver</p>
            </a>
        </div>
    );

    if (showForm) {
        return (
            <div className="min-h-screen bg-white dark:bg-slate-950 px-4 py-8 md:py-16">
                <div className="max-w-xl mx-auto flex flex-col min-h-[85vh]">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="h-12 w-12 rounded-2xl bg-blue-600 overflow-hidden flex items-center justify-center text-white text-lg shadow-lg shadow-blue-500/20 border-2 border-white dark:border-slate-800">
                                {inviteData?.targetMemberPhoto ? (
                                    <img src={inviteData.targetMemberPhoto} alt={inviteData.targetMemberName} className="w-full h-full object-cover" />
                                ) : (
                                    <span>{inviteData?.targetMemberType === 'me' ? '👤' : inviteData?.targetMember === 'partner' ? '💑' : '👶'}</span>
                                )}
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white leading-none">
                                    {inviteData?.targetMemberName || inviteData?.userName}
                                </h2>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">PERFORMANS PLANLAMASI</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="space-y-6">
                                <div className="p-1 px-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pt-3">GÜNLÜK KALORİ *</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.dailyCalories}
                                        onChange={(e) => setFormData({ ...formData, dailyCalories: e.target.value })}
                                        className="w-full bg-transparent h-12 text-2xl font-black text-blue-600 outline-none placeholder:text-slate-200 dark:placeholder:text-slate-800"
                                        placeholder="0000"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-3 px-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">PROTEİN (G) *</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.proteinMin}
                                            onChange={(e) => setFormData({ ...formData, proteinMin: e.target.value })}
                                            className="w-full bg-transparent font-bold text-slate-700 dark:text-slate-300 outline-none"
                                            placeholder="Giriş"
                                        />
                                    </div>
                                    <div className="p-3 px-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">YAĞ MAX (G) *</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.fatMax}
                                            onChange={(e) => setFormData({ ...formData, fatMax: e.target.value })}
                                            className="w-full bg-transparent font-bold text-slate-700 dark:text-slate-300 outline-none"
                                            placeholder="Max"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-3 px-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">KARB MİN (G) *</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.carbsMin}
                                            onChange={(e) => setFormData({ ...formData, carbsMin: e.target.value })}
                                            className="w-full bg-transparent font-bold text-slate-700 dark:text-slate-300 outline-none"
                                            placeholder="Min"
                                        />
                                    </div>
                                    <div className="p-3 px-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">KARB MAX (G) *</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.carbsMax}
                                            onChange={(e) => setFormData({ ...formData, carbsMax: e.target.value })}
                                            className="w-full bg-transparent font-bold text-slate-700 dark:text-slate-300 outline-none"
                                            placeholder="Max"
                                        />
                                    </div>
                                </div>

                                <div className="p-3 px-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-2xl">
                                    <label className="block text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">PORSİYON ÇARPANI (Örn: 1.5)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.portionMultiplier}
                                        onChange={(e) => setFormData({ ...formData, portionMultiplier: e.target.value })}
                                        className="w-full bg-transparent font-bold text-blue-700 dark:text-blue-300 outline-none"
                                        placeholder="1.0"
                                    />
                                </div>

                                <div className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl">
                                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">SPORCUYA ÖZEL NOT</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="w-full bg-transparent text-sm font-medium text-slate-600 dark:text-slate-400 outline-none resize-none h-20"
                                        placeholder="Antrenman planı, öğün zamanlaması..."
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-3xl font-black text-lg transition-all shadow-xl shadow-blue-500/20 active:scale-95 disabled:opacity-50"
                            >
                                {submitting ? (
                                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
                                ) : (
                                    'Hedefleri Mutfağa Gönder'
                                )}
                            </button>
                        </form>

                        <p className="mt-6 text-[10px] text-slate-400 text-center italic">
                            * Girilen değerler bir tıbbi diyet değil, performans optimizasyonudur.
                        </p>
                    </div>

                    <Branding />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-slate-950 px-4 py-12 flex flex-col justify-between">
            <div className="max-w-xl mx-auto flex-1 flex flex-col items-center justify-center w-full">

                {/* Elegant Header */}
                <div className="text-center space-y-4 mb-8">
                    <div className="relative mx-auto mb-6">
                        <div className="h-24 w-24 bg-white dark:bg-slate-900 rounded-[2rem] flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/10 border-4 border-slate-50 dark:border-slate-900 overflow-hidden group">
                            {inviteData?.targetMemberPhoto ? (
                                <img src={inviteData.targetMemberPhoto} alt={inviteData.targetMemberName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                            ) : (
                                <div className="text-4xl">
                                    {inviteData?.targetMember === 'me' ? '👤' : inviteData?.targetMember === 'partner' ? '💑' : '👶'}
                                </div>
                            )}
                        </div>
                        <div className="absolute -bottom-2 -right-2 h-10 w-10 bg-blue-600 rounded-2xl flex items-center justify-center border-4 border-white dark:border-slate-950 shadow-lg p-2">
                            <span className="material-symbols-outlined text-white text-xl">sports_gymnastics</span>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white leading-tight">
                            Antrenör <span className="text-blue-600 italic font-serif">Daveti</span>
                        </h1>
                        <p className="text-slate-500 text-sm font-medium px-4 leading-relaxed">
                            Sporcunuz <span className="text-blue-600 font-bold">{inviteData?.targetMemberName}</span>, mutfağını yönetmeniz için sizi bekliyor.
                        </p>
                    </div>
                </div>

                {/* Main Message Section */}
                <div className="w-full space-y-6 bg-slate-50 dark:bg-slate-900/50 p-6 md:p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm mb-8">
                    <div className="space-y-3">
                        <p className="text-lg font-black text-slate-800 dark:text-slate-200 leading-none">Merhaba Koç,</p>
                        <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                            BBTV ile sporcularınızın mutfağı artık sizin kontrolünüzde. Doğru makrolarla doğru sonuçlar alın.
                        </p>
                    </div>

                    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none border-b border-slate-50 dark:border-slate-800 pb-3">SİSTEM ÖZELLİKLERİ</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            {[
                                { text: 'Kalori hedefleri koyun', icon: 'check_circle_outline' },
                                { text: 'Makro dengesini ayarlayın', icon: 'check_circle_outline' },
                                { text: 'Porsiyon çarpani belirleyin', icon: 'check_circle_outline' },
                                { text: 'Hemen sonuçları görün', icon: 'check_circle_outline' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <span className={`material-symbols-outlined text-[14px] text-blue-500`}>
                                        {item.icon}
                                    </span>
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Primary Actions */}
                <div className="w-full space-y-3 max-w-sm">
                    <button
                        onClick={handleAccept}
                        className="w-full h-16 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[2rem] font-black text-lg shadow-xl shadow-slate-200 dark:shadow-none transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3"
                    >
                        Anladım, Başlayalım
                        <span className="material-symbols-outlined text-sm font-black">arrow_forward</span>
                    </button>
                    <button
                        onClick={handlePostpone}
                        className="w-full h-10 bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors font-bold text-[10px] uppercase tracking-[0.2em]"
                    >
                        Sonra Bakacağım
                    </button>
                    <p className="text-[10px] text-slate-400 text-center italic opacity-60">
                        * Girilen değerler tıbbi tavsiye niteliği taşımaz.
                    </p>
                </div>

            </div>
            <Branding />
        </div>
    );
};

export default PTInvite;
